import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import { Grid } from "@mui/material";
import { Process } from "../../models/lmf.models";
import { CustomButton } from "./Buttons";
import { StatusCodes } from "./constants";
import { useState } from "react";

interface ArriveButtonsProps {
  process: Process;
  hasProcessData: boolean;
  changeHeatStatus: (heatId: number, newStatus: string) => Promise<void>;
  refreshData: () => Promise<void>;
  pause: () => void;
  resume: () => void;
  setShowArrive: (show: boolean) => void;
  setErrorMessage: (message: string) => void;
  saveProcess: (
    process: Process,
    opts?: { isShipManual?: boolean },
  ) => Promise<boolean>;
}

interface ButtonDefinition {
  key: string;
  label: (process: Process) => string;
  isDisabled: (
    process: Process,
    disableButtons: boolean,
    hasProcessData: boolean,
  ) => boolean;
  onClick: (props: ArriveButtonsProps) => Promise<void>;
  confirmMessage?: (process: Process) => string | undefined;
  errorMessage: string;
}

const describeTarget = (process: Process) =>
  process.HeatName ? `heat ${process.HeatName}` : "this process";

export const ArriveButtons = (props: ArriveButtonsProps): JSX.Element => {
  const { process, hasProcessData, setErrorMessage, pause, resume } = props;
  const [disableButtons, setDisableButtons] = useState(false);
  const [pendingButton, setPendingButton] = useState<ButtonDefinition | null>(
    null,
  );

  const handleButtonAction = async (
    action: () => Promise<void>,
    errorMessage: string,
  ) => {
    setDisableButtons(true);
    try {
      await action();
    } catch (error) {
      setErrorMessage(errorMessage);
    } finally {
      setDisableButtons(false);
    }
  };

  const handleClick = async (button: ButtonDefinition) => {
    if (button.confirmMessage?.(process)) {
      pause();
      setPendingButton(button);
      return;
    }
    await handleButtonAction(() => button.onClick(props), button.errorMessage);
  };

  const handleConfirmClose = async (value?: string) => {
    const button = pendingButton;
    setPendingButton(null);
    if (value === "OK" && button) {
      await handleButtonAction(
        () => button.onClick(props),
        button.errorMessage,
      );
    }
    resume();
  };

  const shipBlink = !!process.PendingShipSignalTime && process.End === null;

  const buttons: ButtonDefinition[] = [
    {
      key: "arrive",
      label: (process) => (process.End !== null ? "Arrive" : "Undo Arrive"),
      isDisabled: (process, disableButtons, hasProcessData) => {
        if (disableButtons) return true;

        const isLastProcess = process.NextProcessId === null;
        const canArrive = process.End !== null;
        const canUndoArrive =
          !process.IsOldProcess &&
          process.HeatStatusCode === StatusCodes.ARRIVE &&
          process.End === null &&
          (!hasProcessData || isLastProcess);

        return !(canArrive || canUndoArrive);
      },
      onClick: async ({ process, changeHeatStatus, pause, setShowArrive }) => {
        if (process.HeatStatusCode === StatusCodes.ARRIVE) {
          await changeHeatStatus(process.HeatId!, StatusCodes.TAPPED);
        } else {
          pause();
          setShowArrive(true);
        }
      },
      confirmMessage: (process) =>
        process.HeatStatusCode === StatusCodes.ARRIVE
          ? `Are you sure you want to undo the arrive of ${describeTarget(process)}? The LMF process will be removed.`
          : undefined,
      errorMessage: "Error in Arrive operation",
    },
    {
      key: "ship",
      label: (process) =>
        !process.IsOldProcess && process.End !== null ? "Undo Ship" : "Ship",
      isDisabled: (process, disableButtons, hasProcessData) => {
        if (disableButtons) return true;

        const isLastProcess = process.NextProcessId === null;
        const canShip =
          !process.IsOldProcess &&
          process.End === null &&
          (hasProcessData || isLastProcess);
        const canUndoShip =
          !process.IsOldProcess &&
          process.HeatStatusCode === StatusCodes.SHIP &&
          process.End !== null;

        return !(canShip || canUndoShip);
      },
      onClick: async ({
        process,
        changeHeatStatus,
        refreshData,
        saveProcess,
      }) => {
        if (process.HeatStatusCode === StatusCodes.SHIP) {
          await changeHeatStatus(process.HeatId!, StatusCodes.ARRIVE);
          await refreshData();
        } else {
          if (process.HeatStatusCode === StatusCodes.ARRIVE) {
            await changeHeatStatus(process.HeatId!, StatusCodes.SHIP);
          } else {
            const now = new Date();
            const updatedProcess = { ...process, End: now };
            await saveProcess(updatedProcess, { isShipManual: true });
          }
          await refreshData();
        }
      },
      confirmMessage: (process) =>
        process.HeatStatusCode === StatusCodes.SHIP
          ? `Are you sure you want to undo the ship of ${describeTarget(process)}?`
          : `Are you sure you want to ship ${describeTarget(process)}?`,
      errorMessage: "Error in Ship operation.",
    },
  ];

  return (
    <Grid item xs={12} md={6} sx={{ mt: 0.5 }}>
      <Grid container direction="column" spacing={2}>
        {buttons.map((button) => (
          <Grid item xs={12} key={button.key} sx={{ mt: 0.5 }}>
            <CustomButton
              disabled={button.isDisabled(
                process,
                disableButtons,
                hasProcessData,
              )}
              onClick={() => handleClick(button)}
              label={button.label(process)}
              blink={button.key === "ship" && shipBlink}
            />
          </Grid>
        ))}
      </Grid>
      <ConfirmationDialogRaw
        title="Confirmation"
        message={pendingButton?.confirmMessage?.(process)}
        open={pendingButton !== null}
        onClose={handleConfirmClose}
      />
    </Grid>
  );
};
